/**
 * SaveManager.ts — versioned persistence with migration, corruption recovery,
 * and base64 export/import. Storage is injectable so the whole thing is testable
 * without a browser.
 */
import { newGame, SAVE_VERSION, clampToolLevel, type GameState } from './GameState';
import { safeNumber } from './numbers';
import { ORES } from '../data';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** In-memory storage used by tests and as a fallback when localStorage is absent. */
export class MemoryStorage implements StorageLike {
  private map = new Map<string, string>();
  getItem(key: string): string | null {
    return this.map.has(key) ? (this.map.get(key) as string) : null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
}

const SAVE_KEY = 'mining-tycoon:save';
const BACKUP_KEY = 'mining-tycoon:save.bak';

/**
 * Ordered migrations. `migrations[v]` upgrades a save from version `v` to `v+1`.
 * Each is a tiny pure function and is unit-tested. Add new entries as the schema
 * evolves; never edit historical ones.
 */
export const migrations: Array<(s: any) => any> = [
  // v0 -> v1: (none; v1 is the initial schema)
  undefined as unknown as (s: any) => any,
  // v1 -> v2: add the onboarding field (defaulted). Existing players skip the tutorial.
  (s: any) => ({
    ...s,
    onboarding: { tutorialStep: 0, tutorialDone: true, goalIndex: 0, dismissed: true },
  }),
  // v2 -> v3: active-play + ascension + daily + expanded settings (all defaulted).
  (s: any) => ({
    ...s,
    combo: { count: 0, lastClickAt: 0 },
    abilities: {},
    nugget: { active: false, kind: '', x: 0, y: 0, spawnedAt: 0, expiresAt: 0, nextSpawnAt: 0 },
    ascension: { cores: 0, totalCores: 0, ascensions: 0, perks: {} },
    daily: { lastClaimDay: 0, streak: 0 },
  }),
  // v3 -> v4: automation + challenges (defaulted).
  (s: any) => ({
    ...s,
    automation: {
      autoNugget: false, autoBuy: false, autoPrestige: false, autoAscend: false,
      autoPrestigeThreshold: 100, autoAscendThreshold: 10, lastAutoBuyAt: 0,
    },
    challenges: { activeId: null, startLife: 0, completed: [] },
  }),
];

/** Apply migrations sequentially from the save's version up to the current one. */
export function migrate(raw: any): any {
  let s = raw;
  let v: number = typeof s.version === 'number' ? s.version : 0;
  let fn = migrations[v];
  while (v < SAVE_VERSION && fn) {
    s = fn(s);
    v++;
    s.version = v;
    fn = migrations[v];
  }
  s.version = SAVE_VERSION;
  return s;
}

/**
 * Validate & repair a loaded/migrated object against a fresh game, filling in any
 * missing fields with defaults. Derived values are intentionally recomputed by
 * systems, so we only need structural integrity here.
 */
export function sanitize(raw: any, now: number): GameState {
  const base = newGame(now);
  const s = raw && typeof raw === 'object' ? raw : {};

  const merged: GameState = {
    version: SAVE_VERSION,
    rng: { seed: (s.rng?.seed >>> 0) || base.rng.seed },
    meta: {
      createdAt: safeNumber(s.meta?.createdAt, base.meta.createdAt),
      lastSavedAt: safeNumber(s.meta?.lastSavedAt, now),
      gameTimeMs: Math.max(0, safeNumber(s.meta?.gameTimeMs)),
      playtimeMs: Math.max(0, safeNumber(s.meta?.playtimeMs)),
    },
    economy: { money: Math.max(0, safeNumber(s.economy?.money, base.economy.money)) },
    mining: {
      focusDepth: Math.max(0, Math.floor(safeNumber(s.mining?.focusDepth))),
      maxDepth: Math.max(0, Math.floor(safeNumber(s.mining?.maxDepth))),
      toolLevel: clampToolLevel(safeNumber(s.mining?.toolLevel, 1)),
      ore: sanitizeOreMap(s.mining?.ore),
      ingots: sanitizeOreMap(s.mining?.ingots),
    },
    buildings: sanitizeCountMap(s.buildings),
    workers: sanitizeCountMap(s.workers),
    upgrades: sanitizeBoolMap(s.upgrades),
    market: {
      price: sanitizePriceMap(s.market?.price),
      nextUpdateAt: safeNumber(s.market?.nextUpdateAt),
    },
    research: {
      points: Math.max(0, safeNumber(s.research?.points)),
      researched: sanitizeBoolMap(s.research?.researched),
    },
    prestige: {
      points: Math.max(0, safeNumber(s.prestige?.points)),
      totalEarned: Math.max(0, safeNumber(s.prestige?.totalEarned)),
      resets: Math.max(0, Math.floor(safeNumber(s.prestige?.resets))),
      skills: sanitizeCountMap(s.prestige?.skills),
    },
    modifiers: Array.isArray(s.modifiers) ? s.modifiers.filter(validModifier) : [],
    events: {
      activeId: typeof s.events?.activeId === 'string' ? s.events.activeId : null,
      nextRollAt: safeNumber(s.events?.nextRollAt),
      cooldownUntil: safeNumber(s.events?.cooldownUntil),
    },
    quests: {
      active: Array.isArray(s.quests?.active) ? s.quests.active.filter(validQuest) : [],
      completed: Array.isArray(s.quests?.completed)
        ? s.quests.completed.filter((x: any) => typeof x === 'string')
        : [],
      nextOfferAt: safeNumber(s.quests?.nextOfferAt),
    },
    achievements: sanitizeBoolMap(s.achievements),
    stats: {
      lifetimeEarnings: Math.max(0, safeNumber(s.stats?.lifetimeEarnings)),
      totalOreMined: Math.max(0, safeNumber(s.stats?.totalOreMined)),
      totalClicks: Math.max(0, Math.floor(safeNumber(s.stats?.totalClicks))),
      oreMined: sanitizeOreMap(s.stats?.oreMined),
      oreSold: sanitizeOreMap(s.stats?.oreSold),
      nuggetsClicked: Math.max(0, Math.floor(safeNumber(s.stats?.nuggetsClicked))),
      abilitiesUsed: Math.max(0, Math.floor(safeNumber(s.stats?.abilitiesUsed))),
      maxCombo: Math.max(0, Math.floor(safeNumber(s.stats?.maxCombo))),
    },
    settings: sanitizeSettings(s.settings),
    onboarding: {
      tutorialStep: Math.max(0, Math.floor(safeNumber(s.onboarding?.tutorialStep))),
      tutorialDone: !!s.onboarding?.tutorialDone,
      goalIndex: Math.max(0, Math.floor(safeNumber(s.onboarding?.goalIndex))),
      dismissed: !!s.onboarding?.dismissed,
    },
    combo: {
      count: Math.max(0, Math.floor(safeNumber(s.combo?.count))),
      lastClickAt: Math.max(0, safeNumber(s.combo?.lastClickAt)),
    },
    abilities: sanitizeAbilities(s.abilities),
    nugget: {
      active: !!s.nugget?.active && typeof s.nugget?.kind === 'string' && s.nugget.kind.length > 0,
      kind: typeof s.nugget?.kind === 'string' ? s.nugget.kind : '',
      x: clamp01(safeNumber(s.nugget?.x)),
      y: clamp01(safeNumber(s.nugget?.y)),
      spawnedAt: Math.max(0, safeNumber(s.nugget?.spawnedAt)),
      expiresAt: Math.max(0, safeNumber(s.nugget?.expiresAt)),
      nextSpawnAt: Math.max(0, safeNumber(s.nugget?.nextSpawnAt)),
    },
    ascension: {
      cores: Math.max(0, safeNumber(s.ascension?.cores)),
      totalCores: Math.max(0, safeNumber(s.ascension?.totalCores)),
      ascensions: Math.max(0, Math.floor(safeNumber(s.ascension?.ascensions))),
      perks: sanitizeCountMap(s.ascension?.perks),
    },
    daily: {
      lastClaimDay: Math.max(0, Math.floor(safeNumber(s.daily?.lastClaimDay))),
      streak: Math.max(0, Math.floor(safeNumber(s.daily?.streak))),
    },
    automation: {
      autoNugget: !!s.automation?.autoNugget,
      autoBuy: !!s.automation?.autoBuy,
      autoPrestige: !!s.automation?.autoPrestige,
      autoAscend: !!s.automation?.autoAscend,
      autoPrestigeThreshold: Math.max(1, safeNumber(s.automation?.autoPrestigeThreshold, 100)),
      autoAscendThreshold: Math.max(1, safeNumber(s.automation?.autoAscendThreshold, 10)),
      lastAutoBuyAt: Math.max(0, safeNumber(s.automation?.lastAutoBuyAt)),
    },
    challenges: {
      activeId: typeof s.challenges?.activeId === 'string' ? s.challenges.activeId : null,
      startLife: Math.max(0, safeNumber(s.challenges?.startLife)),
      completed: Array.isArray(s.challenges?.completed)
        ? s.challenges.completed.filter((x: any) => typeof x === 'string')
        : [],
    },
  };

  merged.mining.maxDepth = Math.min(merged.mining.maxDepth, ORES.length - 1);
  merged.mining.focusDepth = Math.min(merged.mining.focusDepth, merged.mining.maxDepth);
  return merged;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}
function clampRange(n: number, lo: number, hi: number, fallback: number): number {
  const v = safeNumber(n, fallback);
  return Math.max(lo, Math.min(hi, v));
}
const NOTATIONS = ['short', 'scientific', 'engineering', 'letters'];
function sanitizeSettings(s: any): GameState['settings'] {
  const themes = Array.isArray(s?.unlockedThemes)
    ? s.unlockedThemes.filter((x: any) => typeof x === 'string')
    : [];
  if (!themes.includes('default')) themes.unshift('default');
  return {
    muted: !!s?.muted,
    notation: NOTATIONS.includes(s?.notation) ? s.notation : 'short',
    autosell: !!s?.autosell,
    masterVolume: clampRange(s?.masterVolume, 0, 1, 0.8),
    musicVolume: clampRange(s?.musicVolume, 0, 1, 0.5),
    sfxVolume: clampRange(s?.sfxVolume, 0, 1, 0.8),
    musicEnabled: s?.musicEnabled === undefined ? true : !!s.musicEnabled,
    theme: typeof s?.theme === 'string' ? s.theme : 'default',
    unlockedThemes: themes,
    reducedMotion: !!s?.reducedMotion,
    particleDensity: clampRange(s?.particleDensity, 0, 1, 1),
    fontScale: clampRange(s?.fontScale, 0.85, 1.4, 1),
  };
}
function sanitizeAbilities(m: any): Record<string, { readyAt: number }> {
  const out: Record<string, { readyAt: number }> = {};
  if (m && typeof m === 'object') {
    for (const [k, v] of Object.entries(m)) {
      out[k] = { readyAt: Math.max(0, safeNumber((v as any)?.readyAt)) };
    }
  }
  return out;
}

function sanitizeOreMap(m: any): Record<string, number> {
  const out: Record<string, number> = {};
  for (const o of ORES) out[o.id] = Math.max(0, safeNumber(m?.[o.id]));
  return out;
}
function sanitizePriceMap(m: any): Record<string, number> {
  const out: Record<string, number> = {};
  for (const o of ORES) {
    const p = safeNumber(m?.[o.id], o.baseValue);
    out[o.id] = p > 0 ? p : o.baseValue;
  }
  return out;
}
function sanitizeCountMap(m: any): Record<string, number> {
  const out: Record<string, number> = {};
  if (m && typeof m === 'object') {
    for (const [k, v] of Object.entries(m)) {
      const n = Math.max(0, Math.floor(safeNumber(v as number)));
      if (n > 0) out[k] = n;
    }
  }
  return out;
}
function sanitizeBoolMap(m: any): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (m && typeof m === 'object') {
    for (const [k, v] of Object.entries(m)) if (v) out[k] = true;
  }
  return out;
}
function validModifier(m: any): boolean {
  return m && typeof m.id === 'string' && m.effect && typeof m.endsAt === 'number';
}
function validQuest(q: any): boolean {
  return q && typeof q.oreId === 'string' && typeof q.amount === 'number';
}

/** Serialize state to a JSON string (the persisted form). */
export function serialize(state: GameState): string {
  return JSON.stringify(state);
}

/** Parse + migrate + sanitize a JSON string into a valid GameState. */
export function deserialize(json: string, now: number): GameState {
  const raw = JSON.parse(json);
  return sanitize(migrate(raw), now);
}

export class SaveManager {
  constructor(private storage: StorageLike) {}

  /** Persist state and stamp lastSavedAt. */
  save(state: GameState, now: number): void {
    state.meta.lastSavedAt = now;
    const json = serialize(state);
    // Keep the previous good save as a backup before overwriting.
    const prev = this.storage.getItem(SAVE_KEY);
    if (prev) this.storage.setItem(BACKUP_KEY, prev);
    this.storage.setItem(SAVE_KEY, json);
  }

  /**
   * Load a save. Returns the state plus how much offline time elapsed since the
   * last save (the caller runs the catch-up). Falls back to a new game on any
   * corruption, backing up the bad blob first.
   */
  load(now: number): { state: GameState; offlineMs: number; fresh: boolean } {
    const json = this.storage.getItem(SAVE_KEY);
    if (!json) return { state: newGame(now), offlineMs: 0, fresh: true };
    try {
      const state = deserialize(json, now);
      const offlineMs = Math.max(0, now - state.meta.lastSavedAt);
      return { state, offlineMs, fresh: false };
    } catch {
      // Corrupt save: preserve it for forensics, then start fresh.
      this.storage.setItem(`${SAVE_KEY}.corrupt`, json);
      return { state: newGame(now), offlineMs: 0, fresh: true };
    }
  }

  hasSave(): boolean {
    return this.storage.getItem(SAVE_KEY) !== null;
  }

  wipe(): void {
    this.storage.removeItem(SAVE_KEY);
    this.storage.removeItem(BACKUP_KEY);
  }

  /** Export the current save as a base64 string for the player to copy. */
  export(state: GameState): string {
    return toBase64(serialize(state));
  }

  /** Import a base64 save string. Throws on failure. */
  import(encoded: string, now: number): GameState {
    return deserialize(fromBase64(encoded.trim()), now);
  }
}

function toBase64(s: string): string {
  if (typeof btoa === 'function') return btoa(unescape(encodeURIComponent(s)));
  return Buffer.from(s, 'utf-8').toString('base64');
}
function fromBase64(s: string): string {
  if (typeof atob === 'function') return decodeURIComponent(escape(atob(s)));
  return Buffer.from(s, 'base64').toString('utf-8');
}
